import type {
  EIntegrationProvider,
  EReasoningMode,
  ESearchMode,
  IAttachmentsOmittedAnswerView,
  IAttachmentsOmittedView,
  IAutoCapabilityOverride,
  IContextPressureView,
  IIndexedSelectionItemAssistant,
  IIndexedSelectionItemModel,
  IPendingFile,
  IUploadFailureDetails,
  TIntegrationIconComponent,
} from "@frontend-shared/types";
import type { IChatMessageLengthState } from "@frontend-shared/utils";
import type { ReactElement } from "react";
export enum EInputIntegrationProvider {
  GOOGLE_DRIVE = "google_drive",
  DROPBOX = "dropbox",
  CONFLUENCE = "confluence",
  ONE_DRIVE = "onedrive",
  NEXTCLOUD = "nextcloud",
  LOCAL_FILE = "local_file",
}

export enum EAttachmentStatus {
  DOWNLOADING = "downloading",
  UPLOADING = "uploading",
  READY = "ready",
  FAILED = "failed",
}

export interface IIntegration {
  id: EIntegrationProvider;
  name: string;
  provider: EInputIntegrationProvider;
  // NOTE: NSAI-6634 — the shared integration-icon type, so a mirrored
  // Pipedream logo from `resolveIntegrationIcon` fits as well as an SVG.
  icon: TIntegrationIconComponent;
  // NOTE: Extra providers that also light this picker entry — e.g. the
  // Confluence Data Center provider shares the cloud Confluence source.
  additionalProviderIds?: EIntegrationProvider[];
}

export interface IAttachment {
  id: string;
  name: string;
  type: string;
  content: string | ArrayBuffer | null;
  provider: EInputIntegrationProvider;
  status: EAttachmentStatus;
  progress?: number;
  // NOTE: NSAI-5132 — bytes pulled from a third-party provider so far. The only
  // progress signal available when the provider stream arrives without a
  // Content-Length, which is what the Uploads row falls back to.
  downloadedBytes?: number;
  // NOTE: Exact provider Content-Length for stable transfer-popup totals.
  totalBytes?: number;
  // NOTE: SHA-256 of the ORIGINAL picked/pasted bytes — the attachment's identity
  // for duplicate detection, so a renamed copy is still recognized and two files
  // that merely share a name are not merged. Deliberately not recomputed when
  // `file` is swapped (HEIC→JPEG conversion) so a re-added .heic still matches.
  // Absent when the browser cannot hash (no `crypto.subtle` outside secure
  // contexts) or the attachment never had a local File — see
  // `filterDuplicateFiles` for the name + size fallback.
  contentHash?: string;
  // NOTE: Byte size of the ORIGINAL picked/pasted file. Only used as part of the
  // fallback identity when `contentHash` is unavailable.
  size?: number;
  // NOTE: Raw file data for S3 upload at send time (local files, HEIC conversions, integration downloads)
  file?: File;
  cancel?: () => void;
  // NOTE: UUID of an existing server-side file (set when editing assistant with existing files)
  existingFileUuid?: string;
  // NOTE: S3 object key produced by a presigned upload. Set on the assistant form flow
  // where files are uploaded to S3 immediately on drop so the key can be submitted later,
  // and (NSAI-4876) on the chat flow once a send has uploaded it, so retrying a sibling
  // does not upload this file a second time and orphan the first object.
  s3Key?: string;
  // NOTE: NSAI-4876 — why this attachment failed, shown on the chip itself. One
  // toast cannot say which of several files died.
  errorMessage?: string;
}

export interface IAttachmentDownloadRequest {
  url: string;
  params: Record<string, unknown>;
  fileName?: string;
  forcedMimeType?: string;
}

export interface IPendingNewChatState {
  content: string;
  files: IPendingFile[];
  models: IIndexedSelectionItemModel[];
  assistants: IIndexedSelectionItemAssistant[];
}

export interface IAttachmentReadyProps {
  attachmentId: string;
  content: string | null;
  type?: string;
  name?: string;
  // NOTE: Replacement File for HEIC→JPEG conversions where the original File is no longer valid
  file?: File;
}

export interface IS3UploadedFile {
  type: string;
  name: string;
  s3Key: string;
}

export interface IErrorResponseBlockProps {
  message: string;
  testId: string;
  isMarkdown?: boolean;
  actionLabel?: string;
  isActionDisabled?: boolean;
  onAction?: () => void;
  // NOTE: NSAI-5810 — headline above the message, for the rows that now lead
  // with what failed and explain underneath. Optional: every existing caller
  // passes a single sentence and keeps the one-line row it has always had.
  title?: string;
  // NOTE: NSAI-5810 — the engine's own wording, rendered collapsed. Only a
  // GENERATION_FAILURE row carries one; a provider failure deliberately does
  // not, since its text is exactly what the server's sanitizers exist to strip.
  technicalDetail?: string;
}

// NOTE: NSAI-5820 — the context-pressure banner's props. `view` is already
// lifted (frontend-shared/utils/context-pressure.ts), so the component decides
// nothing about thresholds — it only renders what the shared lift resolved.
export interface IContextPressureBannerProps {
  view: IContextPressureView;
  isCondensing?: boolean;
  onCondense?: () => void;
}

// NOTE: NSAI-5691 — the composer's standing against MAX_CHAT_MESSAGE_LENGTH.
export interface IMessageLengthNoticeProps {
  lengthState: IChatMessageLengthState;
}

// NOTE: NSAI-6883 — `view` is already lifted
// (frontend-shared/utils/attachments-omitted.ts).
export interface IAttachmentsOmittedBannerProps {
  view: IAttachmentsOmittedView;
  onDismiss: () => void;
}

export interface IAttachmentsOmittedAnswerProps {
  answer: IAttachmentsOmittedAnswerView;
}

export interface IErrorTechnicalDetailsProps {
  detail: string;
  testId?: string;
}

export interface IAttachmentPreviewProps {
  onRemove: (attachmentId: string) => void;
  disabled?: boolean;
  // NOTE: NSAI-4876 — re-attempts one failed upload without sending the
  // message. Optional so the assistant flows, which have no failed chip, keep
  // their existing contract.
  onRetry?: (attachmentId: string) => void;
}

export interface IMarkAttachmentsWithStatusProps {
  targetIds: Set<string>;
  status: EAttachmentStatus;
  progress: number;
}

export interface IUploadAttachmentsResult {
  // NOTE: Files that were successfully uploaded to S3
  uploaded: IS3UploadedFile[];
  // NOTE: NSAI-4876 — failed attachment ids mapped to why they failed. Was a
  // bare `Set<string>`, which discarded the typed reason the shared uploader
  // had already resolved.
  failed: Map<string, IFailedAttachment>;
}

export interface IFailedAttachment {
  // NOTE: NSAI-4583 — the classified reason, kept alongside the rendered
  // sentence so a caller reporting a whole batch can collapse it to one line
  // (`resolveUploadFailureMessage`) instead of repeating one file's story.
  details: IUploadFailureDetails;
  // NOTE: Absent when the error carried no explanation of its own.
  reason?: string;
}

// NOTE: Props for the internal TruncatedBold component used inside ModelChangeInformer
// to bridge the <Trans> children API with TruncatedTextWithTooltip's value prop.
export interface ITruncatedBoldProps {
  children?: React.ReactNode;
}

export interface IBotChatBanner {
  title: string;
  description: string;
  icon: ReactElement;
  testId: string;
}

// NOTE: NSAI-6541 — the search chip shown while Auto is selected.
export interface IAutoSearchModeSelectorProps {
  value: ESearchMode;
  onChange: (mode: ESearchMode) => void;
  disabled?: boolean;
  side?: "top" | "bottom";
}

export interface IResendMessageProps {
  assistantMessageUuid: string;
  // NOTE: NSAI-6541 — set by the one-click Auto override; a plain resend
  // replays the composer's current choices.
  searchModeOverride?: ESearchMode;
  reasoningModeOverride?: EReasoningMode;
  modelsOverride?: IIndexedSelectionItemModel[];
  autoOverride?: IAutoCapabilityOverride;
}
